import { DEFAULT_CHANNEL_URL } from './sendbird-config';
const DEFAULT_USER_TYPE = 'MC';
const OFFICER_USER_TYPES = ['MC', 'OFFICER', 'ADMIN'];
const SOFT_DELETE_KEY = 'softDelete';
const SENT_USER_TYPE_KEY = 'sent_user_type';
const DEFAULT_SENT_USER_TYPE = 'officer';
let groupChannelHandlerCtor = null;
let groupChannelHandlerCtorPromise = null;
export async function connectToSendbird(options) {
    const { appId, userId, authToken } = options;
    const [{ default: SendbirdChat }, { GroupChannelModule }] = await Promise.all([
        import('@sendbird/chat'),
        import('@sendbird/chat/groupChannel')
    ]);
    const client = (await SendbirdChat.init({
        appId,
        modules: [new GroupChannelModule()],
        localCacheEnabled: false
    }));
    const currentUser = await client.connect(userId, authToken);
    return {
        client,
        currentUser
    };
}
export async function fetchGroupChannel(client, channelUrl) {
    if (!client?.groupChannel?.getChannel) {
        return null;
    }
    try {
        const channel = await client.groupChannel.getChannel(channelUrl);
        return channel;
    }
    catch (error) {
        console.warn(`Unable to fetch channel at ${channelUrl}:`, error);
        return null;
    }
}
export function deriveChannelUrl(primaryUserId) {
    if (primaryUserId) {
        return `${DEFAULT_CHANNEL_URL}`;
    }
    return DEFAULT_CHANNEL_URL;
}
export function createSystemMessage(messageId, text, channelUrl) {
    return {
        messageId,
        message: text,
        text,
        sender: 'System',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        messageType: 'system',
        channelUrl,
        sentUserType: 'systembot'
    };
}
export function normalizeSendbirdMessage(rawMessage, fallbackChannelUrl) {
    const rawRequestId = rawMessage?.reqId ?? rawMessage?.requestId ?? rawMessage?.request_id ?? null;
    const rawMessageId = rawMessage?.messageId ?? rawMessage?.message_id;
    let messageId = rawMessageId != null ? String(rawMessageId) : '';
    if (!messageId || messageId === '0') {
        messageId = rawRequestId != null ? String(rawRequestId) : `msg_${Date.now()}`;
    }
    const messageText = rawMessage?.message ?? rawMessage?.text ?? rawMessage?.data ?? '[Unsupported message]';
    const createdAt = Number(rawMessage?.createdAt ?? rawMessage?.created_at ?? Date.now());
    const updatedAt = Number(rawMessage?.updatedAt ?? rawMessage?.updated_at ?? createdAt);
    const channelUrl = rawMessage?.channelUrl ?? rawMessage?.channel_url ?? fallbackChannelUrl ?? DEFAULT_CHANNEL_URL;
    const messageType = (rawMessage?.messageType ?? rawMessage?.type ?? 'user');
    const rawFileName = rawMessage?.name ?? rawMessage?.fileName ?? rawMessage?.filename ?? rawMessage?.file_name;
    const normalizedFileName = typeof rawFileName === 'string' && rawFileName.trim().length ? rawFileName.trim() : null;
    const rawFileUrl = rawMessage?.url ?? rawMessage?.fileUrl ?? rawMessage?.file_url ?? null;
    const normalizedFileUrl = typeof rawFileUrl === 'string' && rawFileUrl.trim().length ? rawFileUrl.trim() : null;
    const rawFileType = rawMessage?.fileType ??
        rawMessage?.file_type ??
        rawMessage?.mimeType ??
        rawMessage?.mime_type ??
        (typeof rawMessage?.type === 'string' && rawMessage.type.includes('/') ? rawMessage.type : null);
    const normalizedFileType = typeof rawFileType === 'string' && rawFileType.trim().length ? rawFileType.trim() : null;
    const rawFileSize = rawMessage?.size ??
        rawMessage?.fileSize ??
        rawMessage?.file_size ??
        rawMessage?.filesize ??
        rawMessage?.file?.size;
    const normalizedFileSize = typeof rawFileSize === 'number'
        ? rawFileSize
        : Number(rawFileSize ?? NaN);
    let normalizedFileThumbnail = null;
    const rawThumbnails = Array.isArray(rawMessage?.thumbnails) ? rawMessage.thumbnails : undefined;
    const rawThumbnailUrls = Array.isArray(rawMessage?.thumbnailUrls)
        ? rawMessage.thumbnailUrls
        : undefined;
    if (rawThumbnails && rawThumbnails.length) {
        const firstThumbnail = rawThumbnails.find((thumb) => thumb && typeof thumb === 'object' && typeof thumb.url === 'string');
        if (firstThumbnail) {
            const urlCandidate = firstThumbnail.url;
            if (typeof urlCandidate === 'string' && urlCandidate.trim().length) {
                normalizedFileThumbnail = urlCandidate.trim();
            }
        }
    }
    else if (rawThumbnailUrls && rawThumbnailUrls.length) {
        const thumbCandidate = rawThumbnailUrls.find((candidate) => {
            if (typeof candidate !== 'string') {
                return false;
            }
            return candidate.trim().length > 0;
        });
        if (typeof thumbCandidate === 'string' && thumbCandidate.trim().length) {
            normalizedFileThumbnail = thumbCandidate.trim();
        }
    }
    const rawSender = rawMessage?.sender ?? rawMessage?.user ?? rawMessage?.userId;
    let sender;
    if (!rawSender) {
        sender = 'Unknown';
    }
    else if (typeof rawSender === 'string') {
        sender = rawSender;
    }
    else {
        sender = normalizeSendbirdMember(rawSender);
    }
    const dataRaw = typeof rawMessage?.data === 'string' ? rawMessage.data : null;
    const parsedData = parseMessageData(dataRaw);
    const softDeleteRaw = parsedData[SOFT_DELETE_KEY];
    const sentUserTypeParsed = normalizeSentUserTypeValue(parsedData[SENT_USER_TYPE_KEY]);
    const softDelete = softDeleteRaw && typeof softDeleteRaw === 'object'
        ? {
            note: String(softDeleteRaw.note ?? ''),
            deletedBy: String(softDeleteRaw.deletedBy ?? ''),
            deletedAt: Number(softDeleteRaw.deletedAt ?? Date.now())
        }
        : null;
    const inferredFromSender = inferSentUserTypeFromSender(sender) ?? inferSentUserTypeFromSender(rawSender);
    const sentUserType = (messageType === 'system' ? 'systembot' : null) ??
        sentUserTypeParsed ??
        inferredFromSender ??
        DEFAULT_SENT_USER_TYPE;
    let normalizedText = messageText;
    if (messageType === 'file') {
        normalizedText = normalizedFileName ? `📎 ${normalizedFileName}` : '📎 File received';
    }
    return {
        messageId,
        message: messageText,
        text: normalizedText,
        sender,
        createdAt,
        updatedAt,
        messageType,
        channelUrl,
        requestId: rawRequestId != null ? String(rawRequestId) : undefined,
        data: dataRaw,
        customType: typeof rawMessage?.customType === 'string' ? rawMessage.customType : null,
        softDelete,
        isSoftDeleted: Boolean(softDelete && softDelete.note),
        sentUserType,
        fileName: normalizedFileName,
        fileUrl: normalizedFileUrl,
        fileType: normalizedFileType,
        fileSize: Number.isFinite(normalizedFileSize) ? normalizedFileSize : null,
        fileThumbnailUrl: normalizedFileThumbnail
    };
}
function normalizeMetadata(raw) {
    if (!raw || typeof raw !== 'object') {
        return {};
    }
    const result = {};
    for (const [key, value] of Object.entries(raw)) {
        result[String(key)] = typeof value === 'string' ? value : value != null ? String(value) : '';
    }
    return result;
}
function parseMessageData(rawData) {
    if (typeof rawData !== 'string' || !rawData.trim()) {
        return {};
    }
    try {
        const parsed = JSON.parse(rawData);
        if (parsed && typeof parsed === 'object') {
            return parsed;
        }
    }
    catch (error) {
        console.warn('Failed to parse message data payload:', error);
    }
    return {};
}
function normalizeUserTypeValue(value) {
    if (typeof value !== 'string') {
        return null;
    }
    const normalized = value.trim().toUpperCase();
    switch (normalized) {
        case 'SYSTEMBOT':
            return 'SYSTEMBOT';
        case 'MC':
            return 'MC';
        case 'OFFICER':
            return 'OFFICER';
        case 'ADMIN':
            return 'ADMIN';
        case 'PARTICIPANT':
            return 'PARTICIPANT';
        default:
            return null;
    }
}
function normalizeSentUserTypeValue(value) {
    if (typeof value !== 'string') {
        return null;
    }
    const normalized = value.trim().toLowerCase();
    switch (normalized) {
        case 'participant':
            return 'participant';
        case 'officer':
            return 'officer';
        case 'systembot':
        case 'system-bot':
        case 'system':
            return 'systembot';
        default:
            return null;
    }
}
function inferSentUserTypeFromSender(sender) {
    if (!sender) {
        return null;
    }
    if (typeof sender === 'string') {
        const normalized = sender.trim().toLowerCase();
        if (normalized === 'participant') {
            return 'participant';
        }
        if (normalized === 'officer') {
            return 'officer';
        }
        if (normalized === 'systembot' || normalized === 'system-bot' || normalized === 'system') {
            return 'systembot';
        }
        return null;
    }
    if (typeof sender !== 'object') {
        return null;
    }
    const source = sender;
    const directUserType = normalizeUserTypeValue(source.userType ?? source.user_type ?? source.usertype ?? source.role);
    if (directUserType) {
        if (directUserType === 'PARTICIPANT') {
            return 'participant';
        }
        if (directUserType === 'SYSTEMBOT') {
            return 'systembot';
        }
        return 'officer';
    }
    const metadata = normalizeMetadata(source.metadata ?? source.metaData ?? null);
    const metadataUserType = normalizeUserTypeValue(metadata.usertype ?? metadata.userType ?? metadata.user_type);
    if (metadataUserType) {
        if (metadataUserType === 'PARTICIPANT') {
            return 'participant';
        }
        if (metadataUserType === 'SYSTEMBOT') {
            return 'systembot';
        }
        return 'officer';
    }
    const idCandidate = typeof source.userId === 'string' ? source.userId : typeof source.user_id === 'string' ? source.user_id : null;
    if (idCandidate) {
        const normalizedId = idCandidate.trim().toLowerCase();
        if (normalizedId === 'system-bot' || normalizedId === 'systembot') {
            return 'systembot';
        }
    }
    return null;
}
function resolveUserType(source, metadata) {
    const candidates = [
        source.userType,
        // Common alternate casings/keys
        source.user_type,
        metadata.usertype,
        metadata.userType,
        metadata.user_type
    ];
    for (const candidate of candidates) {
        const normalized = normalizeUserTypeValue(candidate);
        if (normalized) {
            return normalized;
        }
    }
    return DEFAULT_USER_TYPE;
}
export function normalizeSendbirdMember(rawMember) {
    if (!rawMember || typeof rawMember !== 'object') {
        return {
            userId: 'unknown-member',
            nickname: 'Unknown Member',
            profileUrl: undefined,
            metadata: {},
            isActive: false,
            role: 'member',
            state: 'invited',
            userType: DEFAULT_USER_TYPE,
            isOfficerLike: true
        };
    }
    const source = rawMember;
    const rawUserId = source.userId ?? source.user_id ?? source.id ?? source.uuid ?? 'unknown-member';
    const userId = String(rawUserId).trim() || 'unknown-member';
    const nicknameSource = source.nickname ?? source.userId ?? source.user_id ?? userId;
    const nickname = String(nicknameSource).trim() || userId;
    const profileUrlCandidate = source.profileUrl ?? source.profile_url ?? null;
    const profileUrl = typeof profileUrlCandidate === 'string' && profileUrlCandidate.trim().length > 0
        ? profileUrlCandidate.trim()
        : undefined;
    const rawMetadata = source.metadata ??
        source.metaData ??
        null;
    const metadata = normalizeMetadata(rawMetadata);
    const userType = resolveUserType(source, metadata);
    metadata.usertype = userType;
    const rawIsActive = source.isActive ??
        source.is_active ??
        source.isOnline ??
        source.is_online ??
        null;
    const normalizeBoolean = (value) => {
        if (typeof value === 'boolean') {
            return value;
        }
        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase();
            if (normalized === 'true') {
                return true;
            }
            if (normalized === 'false') {
                return false;
            }
        }
        return null;
    };
    const connectionStatusSource = source.connectionStatus ??
        source.connection_status ??
        metadata.connectionStatus ??
        metadata.connection_status ??
        null;
    const normalizedConnectionStatusRaw = typeof connectionStatusSource === 'string'
        ? connectionStatusSource.trim().toUpperCase()
        : '';
    let connectionStatus;
    switch (normalizedConnectionStatusRaw) {
        case 'ONLINE':
            connectionStatus = 'ONLINE';
            break;
        case 'OFFLINE':
            connectionStatus = 'OFFLINE';
            break;
        case 'UNAVAILABLE':
            connectionStatus = 'UNAVAILABLE';
            break;
        default:
            connectionStatus = normalizedConnectionStatusRaw ? 'UNKNOWN' : undefined;
            break;
    }
    const rawIsActiveNormalized = normalizeBoolean(rawIsActive);
    let isActive = rawIsActiveNormalized ?? true;
    if (connectionStatus === 'ONLINE') {
        isActive = true;
    }
    else if (connectionStatus === 'OFFLINE' || connectionStatus === 'UNAVAILABLE') {
        isActive = false;
    }
    const lastSeenCandidate = source.lastSeenAt ?? source.last_seen_at;
    const lastSeenAt = typeof lastSeenCandidate === 'number' ? lastSeenCandidate : undefined;
    const rawRole = typeof source.role === 'string' ? source.role.toLowerCase() : '';
    const inferredRole = rawRole === 'operator' ? 'operator' : 'member';
    const rawStateSource = typeof source.state === 'string'
        ? source.state
        : typeof source.userState === 'string'
            ? source.userState
            : '';
    const rawState = rawStateSource.toLowerCase();
    let state;
    switch (rawState) {
        case 'invited':
            state = 'invited';
            break;
        case 'left':
            state = 'left';
            break;
        default:
            state = 'joined';
            break;
    }
    const isOfficerLike = OFFICER_USER_TYPES.includes(userType);
    const role = isOfficerLike ? 'operator' : inferredRole;
    return {
        userId,
        nickname,
        profileUrl,
        metadata,
        isActive,
        lastSeenAt,
        role,
        state,
        userType,
        isOfficerLike,
        connectionStatus
    };
}
export function normalizeSendbirdMembers(rawMembers) {
    if (!Array.isArray(rawMembers)) {
        return [];
    }
    return rawMembers
        .map((member) => normalizeSendbirdMember(member))
        .filter((member, index, list) => member.userId &&
        list.findIndex((candidate) => candidate.userId === member.userId) === index)
        .sort((a, b) => {
        const nameDiff = a.nickname.localeCompare(b.nickname, undefined, { sensitivity: 'base' });
        if (nameDiff !== 0) {
            return nameDiff;
        }
        return a.userId.localeCompare(b.userId, undefined, { sensitivity: 'base' });
    });
}
export async function annotateMessageAsSoftDeleted(channel, message, options) {
    const channelAny = channel;
    const isFileMessage = message.messageType === 'file';
    const canUpdateUserMessage = typeof channelAny?.updateUserMessage === 'function';
    const canUpdateFileMessage = typeof channelAny?.updateFileMessage === 'function';
    if (isFileMessage && !canUpdateFileMessage) {
        throw new Error('Sendbird channel cannot update file messages.');
    }
    if (!isFileMessage && !canUpdateUserMessage) {
        throw new Error('Sendbird channel cannot update user messages.');
    }
    const payload = {
        ...parseMessageData(message.data ?? null),
        [SOFT_DELETE_KEY]: {
            note: options.note,
            deletedBy: options.deletedBy,
            deletedAt: Date.now()
        }
    };
    const numericId = Number(message.messageId);
    const targetId = Number.isNaN(numericId) ? message.messageId : numericId;
    try {
        let updated;
        if (isFileMessage) {
            updated = await channelAny.updateFileMessage(targetId, {
                message: message.message,
                data: JSON.stringify(payload)
            });
        }
        else {
            updated = await channelAny.updateUserMessage(targetId, {
                message: message.message,
                data: JSON.stringify(payload)
            });
        }
        return normalizeSendbirdMessage(updated, channel.url);
    }
    catch (error) {
        console.error('Unable to annotate message as deleted in Sendbird:', error);
        throw error;
    }
}
export function extractChannelMembers(channel) {
    if (!channel) {
        return [];
    }
    const rawMembers = channel.members ?? channel.members ?? [];
    const normalizedMembers = normalizeSendbirdMembers(rawMembers ?? []);
    try {
        const toArray = (value) => (Array.isArray(value) ? value : []);
        const rawArray = toArray(rawMembers);
        const deriveRawId = (source) => {
            const candidates = [
                source.userId,
                source.user_id,
                source.uuid,
                source.id
            ];
            for (const candidate of candidates) {
                if (typeof candidate === 'string' && candidate.trim().length) {
                    return candidate.trim();
                }
            }
            return '';
        };
        const rawById = new Map();
        rawArray.forEach((raw) => {
            const key = deriveRawId(raw);
            if (key) {
                rawById.set(key, raw);
            }
        });
        const normalizeBoolean = (value) => {
            if (typeof value === 'boolean') {
                return value;
            }
            if (typeof value === 'string') {
                const normalized = value.trim().toLowerCase();
                if (normalized === 'true') {
                    return true;
                }
                if (normalized === 'false') {
                    return false;
                }
            }
            return null;
        };
        const snapshot = normalizedMembers.map((member) => {
            const raw = rawById.get(member.userId) ?? null;
            const readRawField = (field) => raw && Object.prototype.hasOwnProperty.call(raw, field) ? raw[field] : undefined;
            const rawIsActive = readRawField('isActive');
            const rawIsActiveSnake = readRawField('is_active');
            const rawIsOnline = readRawField('isOnline');
            const rawIsOnlineSnake = readRawField('is_online');
            const rawConnectionStatus = readRawField('connectionStatus') ?? readRawField('connection_status');
            const candidates = [rawIsActive, rawIsActiveSnake, rawIsOnline, rawIsOnlineSnake];
            let rawActiveState = null;
            for (const candidate of candidates) {
                const normalized = normalizeBoolean(candidate);
                if (normalized !== null) {
                    rawActiveState = normalized;
                    break;
                }
            }
            const normalizedIsActive = member.isActive !== false;
            const normalizedConnection = (member.connectionStatus ?? '').toUpperCase();
            const normalizedFromConnection = normalizedConnection === 'ONLINE'
                ? true
                : (normalizedConnection === 'OFFLINE' || normalizedConnection === 'UNAVAILABLE')
                    ? false
                    : null;
            const mismatch = (rawActiveState !== null && rawActiveState !== normalizedIsActive) ||
                (normalizedFromConnection !== null && normalizedFromConnection !== normalizedIsActive);
            return {
                userId: member.userId,
                nickname: member.nickname,
                state: member.state,
                normalizedIsActive,
                normalizedConnectionStatus: member.connectionStatus ?? 'UNKNOWN',
                rawIsActive,
                raw_is_active: rawIsActiveSnake,
                rawIsOnline,
                raw_is_online: rawIsOnlineSnake,
                rawConnectionStatus,
                mismatch
            };
        });
        console.debug('[SendbirdService] channel member activity snapshot', {
            channelUrl: channel.url ?? 'unknown-channel',
            members: snapshot
        });
    }
    catch (error) {
        console.warn('Unable to capture Sendbird member activity snapshot:', error);
    }
    return normalizedMembers;
}
export async function fetchInitialMessages(channel, limit) {
    const normalizedLimit = Math.max(0, Number(limit) || 0);
    if (normalizedLimit === 0) {
        return [];
    }
    const channelUrl = channel.url;
    const channelAny = channel;
    const normalizeList = (rawMessages) => (Array.isArray(rawMessages) ? rawMessages : [])
        .map((msg) => normalizeSendbirdMessage(msg, channelUrl))
        .sort((a, b) => a.createdAt - b.createdAt);
    if (typeof channelAny?.getMessagesByTimestamp === 'function') {
        try {
            const anchorTimestamp = Date.now();
            const rawMessages = await channelAny.getMessagesByTimestamp(anchorTimestamp, {
                prevResultSize: normalizedLimit,
                nextResultSize: 0,
                includeMetaArray: true,
                includeReplies: true,
                includeParentMessageInfo: true,
                reverse: true
            });
            const messages = normalizeList(rawMessages);
            console.debug('[SendbirdService] getMessagesByTimestamp initial load', {
                anchorTimestamp,
                requested: normalizedLimit,
                received: Array.isArray(rawMessages) ? rawMessages.length : 0,
                normalizedCount: messages.length
            });
            if (messages.length) {
                return messages;
            }
        }
        catch (error) {
            console.warn('Unable to fetch messages via getMessagesByTimestamp:', error);
        }
    }
    if (typeof channelAny?.createPreviousMessageListQuery === 'function') {
        try {
            const query = channelAny.createPreviousMessageListQuery({
                limit: normalizedLimit,
                reverse: true,
                includeMetaArray: true
            });
            if (query?.load) {
                const rawMessages = await query.load();
                const messages = normalizeList(rawMessages);
                console.debug('[SendbirdService] previous message query initial load', {
                    requested: normalizedLimit,
                    received: Array.isArray(rawMessages) ? rawMessages.length : 0,
                    normalizedCount: messages.length
                });
                if (messages.length) {
                    return messages;
                }
            }
        }
        catch (error) {
            console.warn('Unable to fetch messages via previous message query:', error);
        }
    }
    return [];
}
export async function fetchOlderMessages(channel, beforeTimestamp, limit) {
    const normalizedLimit = Math.max(0, Number(limit) || 0);
    if (normalizedLimit === 0) {
        return [];
    }
    const safeAnchor = Number.isFinite(beforeTimestamp) ? Math.trunc(beforeTimestamp) : Date.now();
    const anchorTimestamp = safeAnchor > 0 ? safeAnchor : Date.now();
    const channelUrl = channel.url;
    const channelAny = channel;
    const normalizeList = (rawMessages) => (Array.isArray(rawMessages) ? rawMessages : [])
        .map((msg) => normalizeSendbirdMessage(msg, channelUrl))
        .filter((message) => Number(message?.createdAt ?? 0) < anchorTimestamp)
        .sort((a, b) => a.createdAt - b.createdAt);
    if (typeof channelAny?.getMessagesByTimestamp === 'function') {
        try {
            const rawMessages = await channelAny.getMessagesByTimestamp(anchorTimestamp, {
                prevResultSize: normalizedLimit,
                nextResultSize: 0,
                includeMetaArray: true,
                includeReplies: true,
                includeParentMessageInfo: true,
                reverse: true,
                isInclusive: false
            });
            const messages = normalizeList(rawMessages);
            console.debug('[SendbirdService] getMessagesByTimestamp older load', {
                anchorTimestamp,
                requested: normalizedLimit,
                received: Array.isArray(rawMessages) ? rawMessages.length : 0,
                normalizedCount: messages.length
            });
            if (messages.length) {
                return messages;
            }
        }
        catch (error) {
            console.warn('Unable to fetch older messages via getMessagesByTimestamp:', error);
        }
    }
    if (typeof channelAny?.createPreviousMessageListQuery === 'function') {
        try {
            const query = channelAny.createPreviousMessageListQuery({
                limit: normalizedLimit,
                reverse: true,
                includeMetaArray: true,
                includeReplies: true,
                includeParentMessageInfo: true,
                messageTimestamp: anchorTimestamp,
                isInclusive: false
            });
            if (query?.load) {
                const rawMessages = await query.load();
                const messages = normalizeList(rawMessages);
                console.debug('[SendbirdService] previous message query older load', {
                    anchorTimestamp,
                    requested: normalizedLimit,
                    received: Array.isArray(rawMessages) ? rawMessages.length : 0,
                    normalizedCount: messages.length
                });
                if (messages.length) {
                    return messages;
                }
            }
        }
        catch (error) {
            console.warn('Unable to fetch older messages via previous message query:', error);
        }
    }
    return [];
}
export async function sendUserMessage(channel, messageText, options = {}) {
    if (!channel || typeof channel?.sendUserMessage !== 'function') {
        throw new Error('Sendbird channel is not ready to send messages.');
    }
    const requestId = options.requestId ?? `req_${Date.now()}`;
    const payloadData = {
        ...(options.data ?? {}),
        [SENT_USER_TYPE_KEY]: options.sentUserType ?? DEFAULT_SENT_USER_TYPE
    };
    const channelAny = channel;
    return new Promise((resolve, reject) => {
        let settled = false;
        const resolveOnce = (raw) => {
            if (settled) {
                return;
            }
            settled = true;
            try {
                resolve(normalizeSendbirdMessage(raw, channel.url));
            }
            catch (error) {
                reject(error);
            }
        };
        const rejectOnce = (error) => {
            if (settled) {
                return;
            }
            settled = true;
            if (error instanceof Error) {
                reject(error);
            }
            else {
                reject(new Error(String(error ?? 'Failed to send message.')));
            }
        };
        try {
            const handler = channelAny.sendUserMessage({
                message: messageText,
                requestId,
                data: JSON.stringify(payloadData)
            });
            if (handler?.onSucceeded) {
                handler.onSucceeded((message) => resolveOnce(message));
                if (handler.onFailed) {
                    handler.onFailed((error) => rejectOnce(error));
                }
            }
            else if (typeof handler?.then === 'function') {
                handler
                    .then((message) => resolveOnce(message))
                    .catch((error) => rejectOnce(error));
            }
            else {
                resolveOnce(handler);
            }
        }
        catch (error) {
            rejectOnce(error);
        }
    });
}
export function sendFileMessage(channel, options) {
    if (!channel || typeof channel?.sendFileMessage !== 'function') {
        throw new Error('Sendbird channel is not ready to send file messages.');
    }
    const channelAny = channel;
    const requestId = options.requestId ?? `file_${Date.now()}`;
    const payloadData = {
        ...(options.data ?? {}),
        [SENT_USER_TYPE_KEY]: options.sentUserType ?? DEFAULT_SENT_USER_TYPE
    };
    let uploadable = null;
    const messagePromise = new Promise((resolve, reject) => {
        let settled = false;
        const resolveOnce = (raw) => {
            if (settled) {
                return;
            }
            settled = true;
            try {
                if (options.onProgress) {
                    const finalBytes = options.file.size ?? 0;
                    options.onProgress({
                        totalBytes: finalBytes,
                        transferredBytes: finalBytes,
                        percent: 100
                    });
                }
                resolve(normalizeSendbirdMessage(raw, channel.url));
            }
            catch (error) {
                reject(error instanceof Error ? error : new Error(String(error ?? 'Failed to parse file message.')));
            }
        };
        const rejectOnce = (error) => {
            if (settled) {
                return;
            }
            settled = true;
            reject(error instanceof Error ? error : new Error(String(error ?? 'Failed to send file message.')));
        };
        try {
            uploadable = channelAny.sendFileMessage({
                file: options.file,
                fileName: options.file.name,
                fileSize: options.file.size,
                mimeType: options.file.type,
                data: JSON.stringify(payloadData),
                requestId
            }, (event) => {
                if (!options.onProgress) {
                    return;
                }
                const totalBytesCandidate = Number(event?.totalBytes ?? event?.totalByteCount ?? options.file.size ?? 0);
                const transferredCandidate = Number(event?.transferredBytes ?? event?.transferredByteCount ?? 0);
                const totalBytes = Number.isFinite(totalBytesCandidate) && totalBytesCandidate > 0
                    ? totalBytesCandidate
                    : options.file.size ?? 0;
                const transferredBytes = Number.isFinite(transferredCandidate) && transferredCandidate >= 0
                    ? transferredCandidate
                    : 0;
                const percent = totalBytes > 0
                    ? Math.min(100, Math.round((Math.min(transferredBytes, totalBytes) / totalBytes) * 100))
                    : 0;
                options.onProgress({
                    totalBytes,
                    transferredBytes: Math.min(transferredBytes, totalBytes),
                    percent
                });
            });
        }
        catch (error) {
            rejectOnce(error);
            return;
        }
        if (!uploadable) {
            rejectOnce(new Error('Sendbird did not return an upload handler.'));
            return;
        }
        if (uploadable.onSucceeded) {
            uploadable.onSucceeded((message) => resolveOnce(message));
            if (uploadable.onFailed) {
                uploadable.onFailed((error) => rejectOnce(error));
            }
            if (uploadable.onCanceled) {
                uploadable.onCanceled((error) => rejectOnce(error ?? new Error('File upload cancelled.')));
            }
        }
        else if (typeof uploadable.then === 'function') {
            uploadable
                .then((message) => resolveOnce(message))
                .catch((error) => rejectOnce(error));
        }
        else {
            resolveOnce(uploadable);
        }
    });
    const cancel = () => {
        if (uploadable && typeof uploadable.cancel === 'function') {
            try {
                uploadable.cancel();
            }
            catch (error) {
                console.warn('Failed to cancel Sendbird file upload:', error);
            }
        }
    };
    return {
        messagePromise,
        cancel
    };
}
async function loadGroupChannelHandlerCtor() {
    if (groupChannelHandlerCtor) {
        return groupChannelHandlerCtor;
    }
    if (!groupChannelHandlerCtorPromise) {
        groupChannelHandlerCtorPromise = import('@sendbird/chat/groupChannel')
            .then((module) => {
            const ctorCandidate = module.GroupChannelHandler;
            if (typeof ctorCandidate === 'function') {
                groupChannelHandlerCtor = ctorCandidate;
                return groupChannelHandlerCtor;
            }
            return null;
        })
            .catch((error) => {
            console.warn('Unable to load Sendbird GroupChannelHandler class:', error);
            return null;
        });
    }
    const resolvedCtor = await groupChannelHandlerCtorPromise;
    if (resolvedCtor) {
        groupChannelHandlerCtor = resolvedCtor;
    }
    return resolvedCtor;
}
export async function registerGroupChannelHandler(client, channelUrl, callbacks = {}, options = {}) {
    if (!client?.groupChannel?.addGroupChannelHandler) {
        return null;
    }
    const ctor = await loadGroupChannelHandlerCtor();
    if (!ctor) {
        return null;
    }
    const handlerId = options.handlerId ?? `handler_${channelUrl}_${Date.now()}`;
    const handler = new ctor();
    handler.onMessageReceived = (incomingChannel, rawMessage) => {
        if (!isMatchingGroupChannel(incomingChannel, channelUrl)) {
            return;
        }
        callbacks.onMessageReceived?.(rawMessage, incomingChannel ?? null);
    };
    handler.onMessageUpdated = (incomingChannel, rawMessage) => {
        if (!isMatchingGroupChannel(incomingChannel, channelUrl)) {
            return;
        }
        callbacks.onMessageUpdated?.(rawMessage, incomingChannel ?? null);
    };
    handler.onMessageDeleted = (incomingChannel, rawMessage, event) => {
        if (!isMatchingGroupChannel(incomingChannel, channelUrl)) {
            return;
        }
        callbacks.onMessageDeleted?.(rawMessage, incomingChannel ?? null, event);
    };
    handler.onUserJoined = (incomingChannel, member) => {
        if (!isMatchingGroupChannel(incomingChannel, channelUrl)) {
            return;
        }
        callbacks.onUserJoined?.(member, incomingChannel ?? null);
    };
    handler.onUserLeft = (incomingChannel, member) => {
        if (!isMatchingGroupChannel(incomingChannel, channelUrl)) {
            return;
        }
        callbacks.onUserLeft?.(member, incomingChannel ?? null);
    };
    handler.onUserMarkedRead = (incomingChannel, userIds) => {
        if (!isMatchingGroupChannel(incomingChannel, channelUrl)) {
            return;
        }
        const normalizedUserIds = Array.isArray(userIds)
            ? userIds
            : userIds != null
                ? [String(userIds)]
                : [];
        callbacks.onUserMarkedRead?.(incomingChannel ?? null, normalizedUserIds);
    };
    handler.onReadStatusUpdated = (incomingChannel) => {
        if (!isMatchingGroupChannel(incomingChannel, channelUrl)) {
            return;
        }
        callbacks.onReadStatusUpdated?.(incomingChannel ?? null);
    };
    handler.onTypingStatusUpdated = (incomingChannel, typingMembers) => {
        if (!isMatchingGroupChannel(incomingChannel, channelUrl)) {
            return;
        }
        callbacks.onTypingStatusUpdated?.(incomingChannel ?? null, typingMembers);
    };
    client.groupChannel.addGroupChannelHandler(handlerId, handler);
    return handlerId;
}
export function unregisterGroupChannelHandler(client, handlerId) {
    if (!handlerId || !client?.groupChannel?.removeGroupChannelHandler) {
        return false;
    }
    client.groupChannel.removeGroupChannelHandler(handlerId);
    return true;
}
export function isMatchingGroupChannel(channel, expectedUrl) {
    return Boolean(channel?.url && channel.url === expectedUrl);
}
export async function disconnectFromSendbird(client) {
    if (!client?.disconnect) {
        return;
    }
    try {
        await client.disconnect();
    }
    catch (error) {
        console.warn('Unable to disconnect from Sendbird:', error);
    }
}
export async function fetchChannelsUnread(client, channelUrls) {
    const results = await Promise.allSettled(channelUrls.map((url) => fetchGroupChannel(client, url)));
    const map = new Map();
    results.forEach((result, index) => {
        const channelUrl = channelUrls[index];
        if (result.status === 'rejected' || !result.value) {
            return;
        }
        const channel = result.value;
        const lastMessage = channel.lastMessage;
        let lastSenderName = '';
        if (lastMessage) {
            const sender = lastMessage.sender;
            if (typeof sender === 'string') {
                lastSenderName = sender;
            }
            else if (sender && typeof sender === 'object') {
                lastSenderName = sender.nickname || sender.userId || '';
            }
        }
        const rawPreview = lastMessage ? (lastMessage.text || lastMessage.message || '') : '';
        const lastMessagePreview = rawPreview.length > 80 ? rawPreview.slice(0, 77) + '\u2026' : rawPreview;
        map.set(channelUrl, {
            channelUrl,
            channelName: channel.name || channelUrl,
            unreadCount: channel.unreadMessageCount ?? 0,
            lastSenderName,
            lastMessagePreview
        });
    });
    return map;
}
//# sourceMappingURL=sendbird-service.js.map